import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useEffect } from "react";
import type { ScheduleWeek } from "../utils/models";
import { toIsoDate, weekEndOf } from "../utils/scheduleWeeks";
import { scheduleWeekQuery } from "./scheduleCache";

type ScheduleBounds = { start: string; end: string } | null;

const shiftWeeks = (weekStart: string, weeks: number): string => toIsoDate(DateTime.fromISO(weekStart).plus({ weeks }));

// The neighbour weeks are only a head start, so they wait until the browser has drawn the week on screen, and
// moving on before then cancels them: quick clicks through cached weeks don't prefetch every week passed.
// Safari has no requestIdleCallback; there the prefetch waits for the next task instead, as DeferMount does.
const runWhenIdle = (task: () => void): (() => void) => {
	if ("requestIdleCallback" in window) {
		const id = window.requestIdleCallback(task);
		return () => window.cancelIdleCallback(id);
	}
	const id = setTimeout(task, 0);
	return () => clearTimeout(id);
};

const boundsOf = (week: ScheduleWeek): ScheduleBounds =>
	week.scheduleStartDate && week.scheduleEndDate ? { start: toIsoDate(week.scheduleStartDate), end: toIsoDate(week.scheduleEndDate) } : null;

/**
 * Once every week on screen is in, prefetches the week just before and just after while the browser is idle, so
 * moving one week shows at once and only the week beyond is fetched in the background. Never past the department's
 * schedule: its bounds come from the cached schedule week, because only the schedule response carries them.
 */
export const usePrefetchNeighbourWeeks = (
	departmentId: string,
	weekStarts: string[],
	isEveryWeekIn: boolean,
	prefetchWeek: (queryClient: QueryClient, departmentId: string, weekStart: string) => Promise<void>,
): void => {
	const queryClient = useQueryClient();
	const firstWeekStart = weekStarts.at(0);
	const lastWeekStart = weekStarts.at(-1);
	// Only watches the cached week, never fetches it: the schedule hook does that.
	const { data: bounds } = useQuery({
		// DEFAULT: a reversed range has no weeks; the watcher then names no real week and the prefetch below never runs.
		...scheduleWeekQuery(departmentId, firstWeekStart ?? ""),
		enabled: false,
		select: boundsOf,
	});

	useEffect(() => {
		if (!isEveryWeekIn || !bounds || firstWeekStart === undefined || lastWeekStart === undefined) return;
		const neighbours = [shiftWeeks(firstWeekStart, -1), shiftWeeks(lastWeekStart, 1)].filter(
			(weekStart) => weekStart <= bounds.end && weekEndOf(weekStart) >= bounds.start,
		);
		return runWhenIdle(() => {
			for (const weekStart of neighbours) void prefetchWeek(queryClient, departmentId, weekStart);
		});
	}, [isEveryWeekIn, bounds, firstWeekStart, lastWeekStart, departmentId, prefetchWeek, queryClient]);
};
